import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import useRequest from "@/lib/request/useRequest";
import { DSSATSimulation } from "@/types/dssat";
import dayjs from "dayjs";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { Weather } from "@/types/weather";
import { getKeyValue, SortDescriptor, Spinner, Table } from "@heroui/react";
import { TableRow, TableCell, TableBody, TableColumn } from "@heroui/react";
import { TableHeader } from "@heroui/react";
import { useTranslation } from "react-i18next";

function NUptakeTable() {
    const { t } = useTranslation("insights");
    const { fieldId } = useFieldDashboard() ?? {};
    const { useGetRequest } = useRequest();
    const f = useFormContext();
    const [startDate, endDate] = f.watch(["startDate", "endDate"]);

    const COLUMNS = useMemo(() => [
        { key: "date", label: t("tableColumns.date") },
        { key: "stage", label: t("tableColumns.stage") },
        { key: "nitrogen_uptake", label: t("tableColumns.nitrogenUptake") },
        { key: "nitrogen_applied", label: t("tableColumns.nitrogenApplied") },
        { key: "nitrogen_leached", label: t("tableColumns.nitrogenLeached") },
        { key: "precipitation", label: t("tableColumns.precipitation") },
    ], [t]);

    const dssatSimulationsQuery = useGetRequest<DSSATSimulation[]>({
        url: `/fields/${fieldId}/dssat_simulations/`,
        enabled: !!fieldId,
    });
    const data = useMemo(() => {
        return sortArrayByKey(
            dssatSimulationsQuery.data?.filter((d) => {
                const date = dayjs(d.date);
                return (
                    (date.isSame(startDate, "day") ||
                        date.isAfter(startDate)) &&
                    (date.isSame(endDate) || date.isBefore(endDate))
                );
            }) ?? [],
            "date",
        ).map((row) => ({
            ...row,
        }));
    }, [dssatSimulationsQuery.data, startDate, endDate]);

    const weatherQuery = useGetRequest<Weather[]>({
        url: `/fields/${fieldId}/weather/`,
        enabled: !!fieldId,
        params: {
            start_date: dayjs(startDate).format("YYYY-MM-DD"),
            end_date: dayjs(endDate).format("YYYY-MM-DD"),
        },
    });

    const weatherData = useMemo(() => {
        return sortArrayByKey(
            weatherQuery.data?.filter((d) => {
                const date = dayjs(d.date);
                return (
                    (date.isSame(startDate, "day") ||
                        date.isAfter(startDate)) &&
                    (date.isSame(endDate) || date.isBefore(endDate))
                );
            }) ?? [],
            "date",
        );
    }, [endDate, startDate, weatherQuery.data]);

    const mergedData = useMemo(() => {
        const dssatLookup = data.reduce(
            (acc, d) => {
                acc[d.date] = d;
                return acc;
            },
            {} as Record<string, DSSATSimulation>,
        );

        return sortArrayByKey(
            weatherData.map((w) => ({
                ...dssatLookup[w.date],
                ...w,
            })),
            "date",
        );
    }, [data, weatherData]);

    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column: "date",
        direction: "ascending",
    });
    const sortedItems = useMemo(() => {
        const { column, direction } = sortDescriptor;
        if (!column) return mergedData;
        const sorted = sortArrayByKey(
            mergedData,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            column as any,
            direction === "descending" ? "desc" : "asc",
        );
        return sorted;
    }, [mergedData, sortDescriptor]);

    return (
        <Table
            isHeaderSticky
            isVirtualized
            maxTableHeight={500}
            aria-label={t('ariaLabels.trendline')}
            onSortChange={setSortDescriptor}
            sortDescriptor={sortDescriptor}
            classNames={{
                th: "px-[0.25rem]",
                td: "px-[0.25rem]",
                wrapper: "p-0 shadow-none rounded-none",
            }}
        >
            <TableHeader columns={COLUMNS}>
                {(column) => (
                    <TableColumn key={column.key} allowsSorting>
                        {column.label}
                    </TableColumn>
                )}
            </TableHeader>
            <TableBody
                items={sortedItems}
                emptyContent={t("messages.noDataDateRange")}
                isLoading={
                    weatherQuery.isFetching || dssatSimulationsQuery.isFetching
                }
                loadingContent={<Spinner label={t("messages.loading")} />}
            >
                {(item) => (
                    <TableRow
                        key={item.date}
                        className="hover:bg-gray-100 rounded-lg"
                    >
                        {(columnKey) => {
                            return (
                                <TableCell className="text-xs">
                                    {getKeyValue(item, columnKey) ?? "--"}
                                </TableCell>
                            );
                        }}
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );
}

export default NUptakeTable;
